import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { GoogleApiLoaderService } from '@core/helpers/google-lib-loader/google-api-loader.service';

@Injectable({ providedIn: 'root' })
export class GoogleAutocompleteService {

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private googleApiLoader: GoogleApiLoaderService,
  ) {}

  async fetchSuggestions(input: string): Promise<google.maps.places.AutocompleteSuggestion[]> {
    if (isPlatformServer(this.platformId)) {
      return [];
    }

    if (!input || input.trim().length === 0) {
      return [];
    }

    await this.ensureApiLoaded();

    try {
      const sessionToken: google.maps.places.AutocompleteSessionToken = new google.maps.places.AutocompleteSessionToken();
      const request: google.maps.places.AutocompleteRequest = {
        input: input.trim(),
        sessionToken,
        includedPrimaryTypes: [
          'street_address',
          'premise',
          'subpremise',
          'postal_code',
          'route',
        ],
        includedRegionCodes: ['US'],
        language: 'en-US',
      };

      const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      return suggestions;
    } catch (error) {
      console.error('GoogleAutocompleteService: Error fetching suggestions', error);

      return [];
    }
  }

  async getPlaceDetails(
    suggestion: google.maps.places.AutocompleteSuggestion,
  ): Promise<google.maps.places.Place | null> {
    if (isPlatformServer(this.platformId)) {
      return null;
    }

    if (!suggestion?.placePrediction) {
      return null;
    }

    await this.ensureApiLoaded();

    try {
      const place: google.maps.places.Place = suggestion.placePrediction.toPlace();

      await place.fetchFields({
        fields: [
          'addressComponents',
          'location',
          'id',
          'formattedAddress',
        ],
      });

      return place;
    } catch (error) {
      console.error('GoogleAutocompleteService: Error fetching place details', error);

      return null;
    }
  }

  async getPlaceDetailsById(placeId: string): Promise<google.maps.places.Place | null> {
    if (isPlatformServer(this.platformId)) {
      return null;
    }

    if (!placeId) {
      return null;
    }

    await this.ensureApiLoaded();

    try {
      const place: google.maps.places.Place = new google.maps.places.Place({ id: placeId });

      await place.fetchFields({
        fields: [
          'addressComponents',
          'location',
          'id',
          'formattedAddress',
        ],
      });

      return place;
    } catch (error) {
      console.error('GoogleAutocompleteService: Error fetching place details by ID', error);

      return null;
    }
  }

  /**
   * Ensures the Google places API is loaded before making API calls.
   */
  private async ensureApiLoaded(): Promise<void> {
    await this.googleApiLoader.loadLibrary('places');
  }

}
